import React, { useState } from 'react';
import { Input, Textarea, Switch, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import type { WatchpointType, MetricInfo } from '@/api/healthcheck';
import { useAlarmFieldCatalogByType } from '@/api/healthcheck';
import { useMyServices, useServiceZones, serviceLabel } from '@/api/services';
import { useServiceWaapDomainsPaged } from '@/api/manticore/service-waap';
import { ElasticConfigForm } from './ElasticConfigForm';
import { isWaapCapable } from '../../waap/waap-service';
import { brand } from '@/config/brand';

export type ConfigState = Record<string, unknown>;

interface FormProps {
  type: WatchpointType;
  config: ConfigState;
  onChange: (patch: ConfigState) => void;
  showErrors?: boolean;
  /** True when the user selected the merged "WAAP / BI" source card. */
  biSource?: boolean;
  /** Called whenever metric selection changes so the wizard can derive + store the concrete type. */
  onDerivedType?: (t: WatchpointType) => void;
}

// ---- Small typed accessors ------------------------------------------------
const str = (c: ConfigState, k: string) => (c[k] === undefined || c[k] === null ? '' : String(c[k]));
const num = (c: ConfigState, k: string) => (c[k] === undefined || c[k] === null ? '' : String(c[k]));
const arr = (c: ConfigState, k: string) => (Array.isArray(c[k]) ? (c[k] as string[]).join(', ') : '');
const toArr = (v: string) => v.split(',').map((s) => s.trim()).filter(Boolean);

const half: React.CSSProperties = { maxWidth: 420 };

/** Per-type config fields for the create wizard. Mutates `config` via onChange. */
export function WatchpointConfigForm({ type, config: c, onChange, showErrors, biSource, onDerivedType }: FormProps) {
  const set = (patch: ConfigState) => onChange(patch);
  const grid: React.CSSProperties = { display: 'grid', gap: 'var(--vn-space-4)' };

  // Always call hooks unconditionally (Rules of Hooks).
  // waapCatalog: enabled whenever the active type is waap/delivery (covers both biSource and legacy).
  // deliveryCatalog: enabled when biSource (merged picker) or type===delivery (legacy single-type).
  const isWaapOrDelivery = type === 'waap' || type === 'delivery';
  const waapCatalog = useAlarmFieldCatalogByType(isWaapOrDelivery ? 'waap' : undefined);
  const deliveryCatalog = useAlarmFieldCatalogByType(
    isWaapOrDelivery && (biSource || type === 'delivery') ? 'delivery' : undefined,
  );

  // Merged metric_catalog for the BI picker (waap + delivery merged, ordered waap first).
  const biMetricCatalog: MetricInfo[] = biSource
    ? [
        ...(waapCatalog.data?.metric_catalog ?? []),
        ...(deliveryCatalog.data?.metric_catalog ?? []),
      ]
    : [];

  // Legacy grouped picker catalog: for single-type delivery use deliveryCatalog; otherwise waapCatalog.
  const catalog = !biSource && type === 'delivery' ? deliveryCatalog : waapCatalog;
  const catalogMetrics =
    catalog.data?.metrics ??
    (catalog.data?.condition_fields ?? []).filter((f) => f.kind === 'number').map((f) => f.name);
  const metricGroups =
    catalog.data?.metric_groups?.length
      ? catalog.data.metric_groups
      : catalogMetrics.length
        ? [{ dashboard: '', label: '', metrics: catalogMetrics }]
        : [];
  const breakdownMetrics: string[] = catalog.data?.breakdown_metrics ?? [];

  const metricsSel = Array.isArray(c.metrics) ? (c.metrics as string[]) : [];

  // Derive the active source type from the first selected metric (BI mode only).
  const activeType: WatchpointType | null = biSource && metricsSel.length > 0
    ? (biMetricCatalog.find((m) => m.key === metricsSel[0])?.type ?? null)
    : null;

  const toggleMetric = (m: string, info?: MetricInfo) => {
    const isBreakdown = info ? info.kind === 'breakdown' : breakdownMetrics.includes(m);
    const isTimeseries = info?.kind === 'timeseries';
    const exclusive = isBreakdown || isTimeseries;

    if (metricsSel.includes(m)) {
      // Deselect — always allowed.
      const next = metricsSel.filter((x) => x !== m);
      set({ metrics: next });
      // Re-derive type from remaining selection.
      if (biSource && onDerivedType) {
        const nextType = next.length > 0
          ? (biMetricCatalog.find((mi) => mi.key === next[0])?.type ?? 'waap')
          : 'waap'; // provisional fallback until user picks
        onDerivedType(nextType);
      }
    } else {
      // Type-lock: if a metric from a different source is selected, block the click.
      if (biSource && activeType && info && info.type !== activeType) return;

      let next: string[];
      if (exclusive) {
        // Exclusive kinds (timeseries, breakdown): clear everything, keep only this one.
        next = [m];
      } else {
        // Scalar: multi-select, but clear any existing exclusive metrics first.
        const exclusiveKeys = biSource
          ? biMetricCatalog.filter((mi) => mi.kind === 'breakdown' || mi.kind === 'timeseries').map((mi) => mi.key)
          : breakdownMetrics;
        const withoutExclusives = metricsSel.filter((x) => !exclusiveKeys.includes(x));
        next = [...withoutExclusives, m];
      }
      set({ metrics: next });
      if (biSource && onDerivedType && info) {
        onDerivedType(info.type);
      }
    }
  };

  if (type === 'http' || type === 'https') {
    return (
      <div style={grid}>
        <Field label="URL" required error={showErrors && !str(c, 'url') ? 'URL is required.' : undefined}>
          {(p) => <Input {...p} value={str(c, 'url')} onChange={(e) => set({ url: e.target.value })} placeholder="https://example.com/health" invalid={!!(showErrors && !str(c, 'url'))} />}
        </Field>
        <Field label="Method">
          {() => (
            <Select value={str(c, 'method') || 'GET'} onValueChange={(v) => set({ method: v })}>
              <SelectTrigger style={half}><SelectValue /></SelectTrigger>
              <SelectContent>
                {['GET', 'HEAD', 'POST', 'PUT'].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Expected status code" description="HTTP status that counts as healthy (default 200).">
          {(p) => <Input {...p} type="number" value={num(c, 'expected_status')} onChange={(e) => set({ expected_status: e.target.value ? Number(e.target.value) : undefined })} placeholder="200" style={half} />}
        </Field>
        <Field label="Body contains" description="Optional substring the response body must include.">
          {(p) => <Input {...p} value={str(c, 'expected_body_contains')} onChange={(e) => set({ expected_body_contains: e.target.value })} placeholder="ok" style={half} />}
        </Field>
      </div>
    );
  }

  if (type === 'tcp') {
    return (
      <div style={grid}>
        <Field label="Host" required error={showErrors && !str(c, 'host') ? 'Host is required.' : undefined}>
          {(p) => <Input {...p} value={str(c, 'host')} onChange={(e) => set({ host: e.target.value })} placeholder="db.example.com" invalid={!!(showErrors && !str(c, 'host'))} style={half} />}
        </Field>
        <Field label="Port" required error={showErrors && !num(c, 'port') ? 'Port is required.' : undefined}>
          {(p) => <Input {...p} type="number" value={num(c, 'port')} onChange={(e) => set({ port: e.target.value ? Number(e.target.value) : undefined })} placeholder="5432" invalid={!!(showErrors && !num(c, 'port'))} style={half} />}
        </Field>
      </div>
    );
  }

  if (type === 'dns') {
    return (
      <div style={grid}>
        <Field label="Host" required error={showErrors && !str(c, 'host') ? 'Host is required.' : undefined}>
          {(p) => <Input {...p} value={str(c, 'host')} onChange={(e) => set({ host: e.target.value })} placeholder="example.com" invalid={!!(showErrors && !str(c, 'host'))} style={half} />}
        </Field>
        <Field label="Record type">
          {() => (
            <Select value={str(c, 'record_type') || 'A'} onValueChange={(v) => set({ record_type: v })}>
              <SelectTrigger style={half}><SelectValue /></SelectTrigger>
              <SelectContent>
                {['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS'].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Expected values" description="Comma-separated values the record must resolve to.">
          {(p) => <Input {...p} value={arr(c, 'expected_values')} onChange={(e) => set({ expected_values: toArr(e.target.value) })} placeholder="1.2.3.4, 5.6.7.8" />}
        </Field>
        <Field label="Nameserver" description="Optional resolver override.">
          {(p) => <Input {...p} value={str(c, 'nameserver')} onChange={(e) => set({ nameserver: e.target.value })} placeholder="8.8.8.8" style={half} />}
        </Field>
      </div>
    );
  }

  if (type === 'tls') {
    return (
      <div style={grid}>
        <Field label="Host" required error={showErrors && !str(c, 'host') ? 'Host is required.' : undefined}>
          {(p) => <Input {...p} value={str(c, 'host')} onChange={(e) => set({ host: e.target.value })} placeholder="example.com" invalid={!!(showErrors && !str(c, 'host'))} style={half} />}
        </Field>
        <Field label="Port">
          {(p) => <Input {...p} type="number" value={num(c, 'port')} onChange={(e) => set({ port: e.target.value ? Number(e.target.value) : undefined })} placeholder="443" style={half} />}
        </Field>
        <Field label="Expiry threshold (days)" description="Alert when the certificate expires within this many days.">
          {(p) => <Input {...p} type="number" value={num(c, 'expiry_threshold_days')} onChange={(e) => set({ expiry_threshold_days: e.target.value ? Number(e.target.value) : undefined })} placeholder="30" style={half} />}
        </Field>
        <Field label="Verify hostname" orientation="horizontal">
          {() => <Switch checked={c.verify_hostname !== false} onCheckedChange={(v) => set({ verify_hostname: v })} />}
        </Field>
      </div>
    );
  }

  // Elastic = structured WAF-log aggregation builder (registry-driven, not KQL).
  if (type === 'elastic') {
    return <ElasticConfigForm config={c} onChange={onChange} showErrors={showErrors} />;
  }

  // Brobe = custom probe-engine query (free-text).
  if (type === 'brobe') {
    return (
      <div style={grid}>
        <Field label="Query" required error={showErrors && !str(c, 'query') ? 'Query is required.' : undefined}>
          {(p) => <Textarea {...p} value={str(c, 'query')} onChange={(e) => set({ query: e.target.value })} placeholder="probe query" rows={3} invalid={!!(showErrors && !str(c, 'query'))} />}
        </Field>
      </div>
    );
  }

  // waap | delivery (biSource=true → merged BI picker; biSource=false → legacy single-type picker)
  return (
    <div style={grid}>
      <EntityServicePicker config={c} set={set} showErrors={showErrors} />
      <DomainsPicker config={c} set={set} />

      {biSource && biMetricCatalog.length > 0 ? (
        // ── Merged BI picker: grouped by kind, with type-lock & unit hints ──
        <BiMetricPicker
          catalog={biMetricCatalog}
          selected={metricsSel}
          activeType={activeType}
          onToggle={(key, info) => toggleMetric(key, info)}
          onClear={() => {
            set({ metrics: [] });
            if (onDerivedType) onDerivedType('waap'); // reset to provisional
          }}
        />
      ) : (
        // ── Legacy grouped picker (single-type, or metric_catalog not yet available) ──
        <Field
          label="Metrics"
          description={
            catalog.isLoading
              ? 'Loading available metrics…'
              : catalogMetrics.length
                ? 'Pick the metrics to monitor (empty = all). Grouped by source dashboard.'
                : 'No selectable metrics for this type — leave empty to fetch all.'
          }
        >
          {() => (
            <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
              {metricGroups.map((g) => (
                <div key={g.dashboard || 'all'} style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                  {g.label && (
                    <div style={{ fontSize: 'var(--vn-font-size-xs)', fontWeight: 600, color: 'var(--vn-fg-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {g.label}
                    </div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
                    {g.metrics.map((m) => {
                      const active = metricsSel.includes(m);
                      const isBreakdown = breakdownMetrics.includes(m);
                      return (
                        <button
                          key={m}
                          type="button"
                          onClick={() => toggleMetric(m)}
                          style={{
                            cursor: 'pointer', font: 'inherit', fontSize: 'var(--vn-font-size-sm)',
                            padding: 'var(--vn-space-1) var(--vn-space-3)', borderRadius: 'var(--vn-radius-full)',
                            border: `1px solid ${active ? 'var(--vn-primary-border)' : 'var(--vn-border-default)'}`,
                            background: active ? 'var(--vn-primary-subtle)' : 'var(--vn-bg-surface)',
                            color: 'var(--vn-fg-default)',
                            display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)',
                          }}
                        >
                          {m}
                          {isBreakdown && (
                            <span style={{
                              fontSize: 'var(--vn-font-size-xs)', lineHeight: 1,
                              padding: '1px 4px', borderRadius: 'var(--vn-radius-sm)',
                              background: 'var(--vn-warning-subtle)', color: 'var(--vn-warning-fg)',
                              fontWeight: 600, letterSpacing: '0.02em',
                            }}>
                              per-entity
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Field>
      )}

      {!biSource && type === 'waap' && metricsSel.length === 0 && (
        <Field label="Dashboard" description="Only when no metrics are picked — fetches the whole dashboard.">
          {(p) => <Input {...p} value={str(c, 'dashboard')} onChange={(e) => set({ dashboard: e.target.value })} placeholder="ai_waf" style={half} />}
        </Field>
      )}
      <Field label="Time range">
        {() => (
          <Select value={str(c, 'time_range') || 'Last 7 days'} onValueChange={(v) => set({ time_range: v })}>
            <SelectTrigger style={half}><SelectValue /></SelectTrigger>
            <SelectContent>
              {['Last 24 hours', 'Last 7 days', 'Last 30 days'].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </Field>
    </div>
  );
}


// ---- Unit label helpers ---------------------------------------------------

const UNIT_LABELS: Record<string, string> = {
  bytes: 'MB',
  bytes_per_sec: 'MB/s',
  ratio: '%',
  rps: 'rps',
};

function unitHint(unit: MetricInfo['unit']): string | null {
  return UNIT_LABELS[unit] ?? null;
}

// ---- BiMetricPicker -------------------------------------------------------

/** The three kind-groups in display order. */
const KIND_GROUPS: Array<{ kind: MetricInfo['kind']; heading: string }> = [
  { kind: 'timeseries', heading: 'Trend (choose one)' },
  { kind: 'scalar', heading: 'Numbers (choose any)' },
  { kind: 'breakdown', heading: 'Per-entity breakdown (choose one)' },
];

interface BiMetricPickerProps {
  catalog: MetricInfo[];
  selected: string[];
  /** The source type locked in by the first selection (null = nothing selected yet). */
  activeType: WatchpointType | null;
  onToggle: (key: string, info: MetricInfo) => void;
  onClear: () => void;
}

function BiMetricPicker({ catalog, selected, activeType, onToggle, onClear }: BiMetricPickerProps) {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
          {activeType
            ? `Tracking ${activeType === 'waap' ? 'Security' : 'Delivery'} metrics — one group per monitor, clear to switch.`
            : 'Pick metrics — a monitor tracks one group (Security or Delivery).'}
        </div>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={onClear}
            style={{
              font: 'inherit', fontSize: 'var(--vn-font-size-xs)', cursor: 'pointer',
              color: 'var(--vn-fg-muted)', background: 'none', border: 'none',
              textDecoration: 'underline', padding: 0, whiteSpace: 'nowrap',
            }}
          >
            Clear
          </button>
        )}
      </div>

      {KIND_GROUPS.map(({ kind, heading }) => {
        const items = catalog.filter((m) => m.kind === kind);
        if (items.length === 0) return null;
        return (
          <div key={kind} style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            <div style={{
              fontSize: 'var(--vn-font-size-xs)', fontWeight: 600,
              color: 'var(--vn-fg-muted)', textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              {heading}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
              {items.map((info) => {
                const active = selected.includes(info.key);
                const locked = !active && activeType !== null && info.type !== activeType;
                const hint = unitHint(info.unit);
                return (
                  <button
                    key={info.key}
                    type="button"
                    title={locked ? 'Different data source — clear selection to switch' : undefined}
                    onClick={() => !locked && onToggle(info.key, info)}
                    style={{
                      cursor: locked ? 'not-allowed' : 'pointer',
                      font: 'inherit', fontSize: 'var(--vn-font-size-sm)',
                      padding: 'var(--vn-space-1) var(--vn-space-3)',
                      borderRadius: 'var(--vn-radius-full)',
                      border: `1px solid ${active ? 'var(--vn-primary-border)' : 'var(--vn-border-default)'}`,
                      background: active ? 'var(--vn-primary-subtle)' : locked ? 'var(--vn-bg-subtle)' : 'var(--vn-bg-surface)',
                      color: locked ? 'var(--vn-fg-disabled)' : 'var(--vn-fg-default)',
                      opacity: locked ? 0.5 : 1,
                      display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)',
                    }}
                  >
                    <span>{info.label}</span>
                    {hint && (
                      <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                        · {hint}
                      </span>
                    )}
                    <span style={{
                      fontSize: 'var(--vn-font-size-xs)', lineHeight: 1,
                      padding: '1px 4px', borderRadius: 'var(--vn-radius-sm)',
                      background: 'var(--vn-neutral-subtle)', color: 'var(--vn-fg-muted)',
                      fontWeight: 500,
                    }}>
                      {info.category}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---- DomainsPicker -------------------------------------------------------

interface DomainsPickerProps {
  config: ConfigState;
  set: (patch: ConfigState) => void;
}

/**
 * Replaces the free-text "Domains" field in the waap|delivery branch.
 * When a service_id is set and WAAP websites are available, shows a chip
 * multiselect. Falls back to free-text (comma-separated) input otherwise,
 * or when the user toggles to manual mode.
 */
function DomainsPicker({ config: c, set }: DomainsPickerProps) {
  const serviceId = typeof c.service_id === 'string' ? c.service_id : '';
  const selected = Array.isArray(c.domains) ? (c.domains as string[]) : [];
  const [manual, setManual] = useState(false);

  // Unconditional hook call (Rules of Hooks). Disabled when serviceId is empty.
  const { data, isLoading } = useServiceWaapDomainsPaged(serviceId, { page: 1, perPage: 100, enabled: true });
  const websites = (data?.data ?? []).map((d) => d.domain).filter(Boolean);

  const showPicker = !manual && !!serviceId && !isLoading && websites.length > 0;

  const toggleDomain = (domain: string) => {
    const next = selected.includes(domain)
      ? selected.filter((d) => d !== domain)
      : [...selected, domain];
    set({ domains: next });
  };

  if (showPicker) {
    return (
      <Field
        label="Domains"
        description={
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
            <span>Pick the websites to monitor.</span>
            <ManualToggle label="Enter manually" onClick={() => setManual(true)} />
          </span>
        }
      >
        {() => (
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
              {websites.map((domain) => {
                const active = selected.includes(domain);
                return (
                  <button
                    key={domain}
                    type="button"
                    onClick={() => toggleDomain(domain)}
                    style={{
                      cursor: 'pointer', font: 'inherit', fontSize: 'var(--vn-font-size-sm)',
                      padding: 'var(--vn-space-1) var(--vn-space-3)', borderRadius: 'var(--vn-radius-full)',
                      border: `1px solid ${active ? 'var(--vn-primary-border)' : 'var(--vn-border-default)'}`,
                      background: active ? 'var(--vn-primary-subtle)' : 'var(--vn-bg-surface)',
                      color: 'var(--vn-fg-default)',
                    }}
                  >
                    {domain}
                  </button>
                );
              })}
            </div>
            {selected.length > 0 && (
              <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                {selected.length} selected
              </div>
            )}
          </div>
        )}
      </Field>
    );
  }

  // Loading state (service selected, fetch in flight)
  if (!manual && !!serviceId && isLoading) {
    return (
      <Field label="Domains" description="Comma-separated domains.">
        {() => (
          <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            Loading websites…
          </div>
        )}
      </Field>
    );
  }

  // Free-text fallback: manual mode, no serviceId, or fetched-but-empty websites list.
  // Only show the picker toggle when the user manually switched away (has websites to go back to).
  const canSwitchToPicker = manual && !!serviceId && websites.length > 0;
  return (
    <Field
      label="Domains"
      description={
        canSwitchToPicker ? (
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
            <span>Comma-separated domains.</span>
            <ManualToggle label="Pick from service websites" onClick={() => setManual(false)} />
          </span>
        ) : (
          'Comma-separated domains.'
        )
      }
    >
      {(p) => (
        <Input
          {...p}
          value={arr(c, 'domains')}
          onChange={(e) => set({ domains: toArr(e.target.value) })}
          placeholder="x.com, y.com"
        />
      )}
    </Field>
  );
}

// ---- EntityServicePicker -------------------------------------------------

interface EntityServicePickerProps {
  config: ConfigState;
  set: (patch: ConfigState) => void;
  showErrors?: boolean;
}

/** Muted inline "Enter Entity ID manually" ⇄ "Pick from service list" toggle link. */
function ManualToggle({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        font: 'inherit', fontSize: 'var(--vn-font-size-xs)', cursor: 'pointer',
        color: 'var(--vn-fg-muted)', background: 'none', border: 'none',
        textDecoration: 'underline', padding: 0, whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}

/**
 * Replaces the free-text Entity input in the waap|delivery branch.
 * Service list (filtered to the product: WAAP=cobra / CDN=eagle) → auto-resolve
 * uid (1 zone) or second Select (>1 zones). Manual fallback + support alert; a
 * "not onboarded" state when the customer has no matching service.
 */
function EntityServicePicker({ config: c, set, showErrors }: EntityServicePickerProps) {
  const [selectedServiceId, setSelectedServiceId] = useState<string>(
    typeof c.service_id === 'string' ? c.service_id : '',
  );
  const [manual, setManual] = useState(false);

  const { data: services = [], isLoading: servicesLoading } = useMyServices();
  // Every BI dashboard here — including the "delivery" one — belongs to the WAAP
  // product, and the WAAP service's entity serves them all. So the service is ALWAYS
  // the WAAP (Conversant / cobra) one, regardless of which metric's dashboard is picked.
  // Hides unrelated services (CDN, k8s, s3…).
  const relevantServices = services.filter((s) => isWaapCapable(s));
  const { data: zones = [], isLoading: zonesLoading } = useServiceZones(selectedServiceId || undefined);

  // Filter zones to those with a non-empty uid.
  const validZones = zones.filter((z) => z.uid && z.uid.trim() !== '');

  const customerId = str(c, 'customer_id');
  const entityError = showErrors && !customerId ? 'Entity is required.' : undefined;

  const handleServiceChange = (serviceId: string) => {
    setSelectedServiceId(serviceId);
    set({ service_id: serviceId, customer_id: '' });
  };

  // Auto-fill when exactly one valid zone.
  React.useEffect(() => {
    if (!selectedServiceId || zonesLoading) return;
    if (validZones.length === 1 && !customerId) {
      set({ customer_id: validZones[0].uid });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServiceId, zonesLoading, validZones.length]);

  if (manual) {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        <Field
          label="Entity"
          required
          error={entityError}
          description={
            <span style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
              <span>Enter your WAAP entity ID directly.</span>
              <ManualToggle label="Pick from service list" onClick={() => setManual(false)} />
            </span>
          }
        >
          {(p) => <Input {...p} value={customerId} onChange={(e) => set({ customer_id: e.target.value })} placeholder="35886" invalid={!!entityError} style={half} />}
        </Field>
        <Alert tone="info" title="Not sure of your Entity ID?">
          Contact <a href={`mailto:${brand.emails.support}`}>{brand.emails.support}</a> for guidance.
        </Alert>
      </div>
    );
  }

  // Customer has no service of this product provisioned in the console yet.
  if (!servicesLoading && relevantServices.length === 0) {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        <Alert tone="warning" title="No WAAP service onboarded">
          You haven&apos;t onboarded a WAAP service in the partner console yet. Contact{' '}
          <a href={`mailto:${brand.emails.support}`}>{brand.emails.support}</a> to get started — or enter your Entity ID manually.
        </Alert>
        <ManualToggle label="Enter Entity ID manually" onClick={() => setManual(true)} />
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      {/* Service selector */}
      <Field
        label="Service"
        required
        description={
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
            <span>{servicesLoading ? 'Loading services…' : 'Select the service that provides the entity.'}</span>
            <ManualToggle label="Enter Entity ID manually" onClick={() => setManual(true)} />
          </span>
        }
      >
        {() => (
          <Select
            value={selectedServiceId || ''}
            onValueChange={handleServiceChange}
          >
            <SelectTrigger style={half}>
              <SelectValue placeholder="Select a service…" />
            </SelectTrigger>
            <SelectContent>
              {relevantServices.map((s) => (
                <SelectItem key={s.id} value={s.id}>{serviceLabel(s)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>

      {/* Entity resolution after a service is chosen */}
      {selectedServiceId && (
        <>
          {zonesLoading && (
            <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
              Loading entities…
            </div>
          )}

          {!zonesLoading && validZones.length === 0 && (
            <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
              This service has no WAAP/CDN entity — pick another service.
            </div>
          )}

          {!zonesLoading && validZones.length === 1 && (
            <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
              Entity: <strong style={{ color: 'var(--vn-fg-default)' }}>{validZones[0].uid}</strong>
            </div>
          )}

          {!zonesLoading && validZones.length > 1 && (
            <Field
              label="Entity"
              required
              error={entityError}
              description="Select the WAAP/CDN entity for this service."
            >
              {() => (
                <Select
                  value={customerId || ''}
                  onValueChange={(uid) => set({ customer_id: uid })}
                >
                  <SelectTrigger style={half} data-invalid={!!entityError}>
                    <SelectValue placeholder="Select entity…" />
                  </SelectTrigger>
                  <SelectContent>
                    {validZones.map((z) => (
                      <SelectItem key={z.uid} value={z.uid}>{z.uid}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          )}
        </>
      )}

      {/* Required error when no service selected yet */}
      {entityError && !selectedServiceId && (
        <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-danger-fg)' }}>
          {entityError}
        </div>
      )}

    </div>
  );
}

/**
 * Fill type-specific config defaults right before submit, mirroring the backend.
 * The DNS "Record type" select only shows "A" as a placeholder default — it is not
 * written into state unless the user opens the dropdown — so seed it here to avoid
 * sending a config without record_type (backend would reject as "unsupported").
 */
export function applyConfigDefaults(type: WatchpointType, c: ConfigState): ConfigState {
  if (type === 'dns' && !str(c, 'record_type')) {
    return { ...c, record_type: 'A' };
  }
  return c;
}

/** Minimum required fields are present for the given type (gate the wizard). */
export function isConfigValid(
  type: WatchpointType,
  c: ConfigState,
  breakdownMetrics: string[] = [],
): boolean {
  switch (type) {
    case 'http':
    case 'https':
      return !!str(c, 'url');
    case 'tcp':
      return !!str(c, 'host') && !!num(c, 'port');
    case 'dns':
    case 'tls':
      return !!str(c, 'host');
    case 'elastic': {
      const hasDomains = Array.isArray(c.domains) && (c.domains as unknown[]).length > 0;
      const metric = (c.metric as string) || 'count';
      return hasDomains && (metric === 'count' || !!str(c, 'metric_field'));
    }
    case 'brobe':
      return !!str(c, 'query');
    case 'waap': {
      if (!str(c, 'customer_id')) return false;
      const sel = Array.isArray(c.metrics) ? (c.metrics as string[]) : [];
      const hasBreakdown = sel.some((m) => breakdownMetrics.includes(m));
      // Breakdown metric must be the only selected metric.
      if (hasBreakdown) return sel.length === 1;
      // Scalar metrics: any selection is valid; otherwise fall back to dashboard.
      return sel.length > 0 || !!str(c, 'dashboard');
    }
    case 'delivery': {
      if (!str(c, 'customer_id')) return false;
      const sel = Array.isArray(c.metrics) ? (c.metrics as string[]) : [];
      const hasBreakdown = sel.some((m) => breakdownMetrics.includes(m));
      // Breakdown metric must be the only selected metric.
      if (hasBreakdown) return sel.length === 1;
      return true;
    }
    default:
      return false;
  }
}
